/**
 * @file actor.ts
 * @author vishwesh5544
 * @description Utility functions for handling actor/user profile data mapping between PHP and Node.js
 * @created May 28, 2025
 * @lastModified May 28, 2025
 * @copyright NextGen 2025
 */

import { ActorProfile2, ActorProfileMaster, ActorUserSetting } from "../types/model/actor";
import { EmpConfigJson } from "../types/model/misc";

/**
 * Map of numeric fields in ActorProfile2 that need to be parsed from strings
 * These fields are received as strings from PHP API but need to be converted to numbers
 * @constant
 * @type {readonly string[]}
 */
const numericFields: readonly string[] = [
    'user_account_id',
    'default_site_id', 
    'default_work_code_number',
    'id',
    'os_emp_type',
    'os_emp_ftpt_type',
    'os_emp_hourly_rate',
    'os_emp_privileges', 
    'os_emp_sch_publish_notification',
    'os_emp_sch_available',
    'timeclock_os_emp_pin',
    'webclock_os_emp_pin',
    'os_emp_account_sequence',
    'os_emp_restrict_clockio',
    'automatic_lunch_deduction_off',
    'punch_rounding_min',
    'company_id',
    'emp_healthcoverage',
    'os_emp_workweek_start_day'
] as const;

/**
 * Mapping of PHP snake_case field names to Node.js camelCase field names for ActorProfile2
 * This ensures consistent field naming between backend and frontend
 * @constant
 * @type {Record<string, keyof ActorProfile2>}
 */
const actorProfile2FieldMap: Record<string, keyof ActorProfile2> = {
    'os_emp_account_id': 'id',
    'user_account_id': 'userAccountId',
    'manager_os_emp_account_id': 'managerOsEmpAccountId',
    'xxus_user_site_profile_id': 'xxusUserSiteProfileId',
    'os_emp_password': 'osEmpPassword',
    'os_emp_color': 'osEmpColor',
    'os_emp_schedule_skills_id': 'osEmpScheduleSkillsId',
    'os_employee_role_code': 'osEmpRoleCode',
    'os_emp_type': 'osEmpType',
    'os_emp_ftpt_type': 'osEmpFtptType',
    'os_emp_hourly_rate': 'osEmpHourlyRate',
    'os_emp_annual_salary': 'osEmpAnnualSalary',
    'os_emp_score': 'osEmpScore',
    'os_emp_birthdate': 'osEmpBirthDate',
    'os_emp_notes': 'osEmpNotes',
    'os_emp_ssn': 'osEmpSsn',
    'os_emp_first_name': 'osEmpFirstName',
    'os_emp_middle_name': 'osEmpMiddleName',
    'os_emp_last_name': 'osEmpLastName',
    'os_emp_email': 'osEmpEmail',
    'os_emp_phone': 'osEmpPhone',
    'os_emp_fax': 'osEmpFax',
    'os_emp_address1': 'osEmpAddress1',
    'os_emp_address2': 'osEmpAddress2',
    'os_emp_city': 'osEmpCity',
    'os_emp_state': 'osEmpState',
    'os_emp_country': 'osEmpCountry',
    'os_emp_zip': 'osEmpZip',
    'os_emp_privileges': 'osEmpPrivileges',
    'os_emp_min_hours': 'osEmpMinHours',
    'os_emp_max_hours': 'osEmpMaxHours',
    'xxxos_emp_schedule_skills_profile_id': 'xxxosEmpScheduleSkillsProfileId',
    'os_emp_sch_publish_notification': 'osEmpSchPublichNotification',
    'os_emp_sch_available': 'osEmpSchAvailable',
    'os_emp_gender': 'osEmpGender',
    'os_emp_hire_date': 'osEmpHireDate',
    'os_emp_termination_date': 'osEmpTerminationDate',
    'os_emp_isactive': 'osEmpIsactive',
    'os_emp_pager': 'osEmpPager',
    'os_emp_biz_employee_number': 'osEmpBizEmployeeNumber',
    'timeclock_os_emp_pin': 'timeclockOsEmpPin',
    'webclock_os_emp_pin': 'webclockOsEmpPin',
    'os_emp_hourly_rate_overtime': 'osEmpHourlyRateOvertime',
    'os_emp_account_sequence': 'osEmpAccountSequence',
    'os_emp_image_mime': 'osEmpImageMime',
    'os_emp_restrict_clockio': 'osEmpRestrictClockio',
    'os_department_id': 'osDepartmentId',
    'department_code': 'departmentCode',
    'default_site_id': 'defaultSiteId',
    'default_work_code_number': 'defaultWorkCodeNumber',
    'automatic_lunch_deduction_off': 'automaticLunchDeductionOff',
    'auto_lunch_deduction_minutes': 'autoLunchDeductionMinutes',
    'auto_lunch_work_hours': 'autoLunchWorkHours',
    'punch_rounding_min': 'punchRoundingMin',
    'substituting_for_emp_id': 'substitutingForEmpId',
    'substitute_type': 'substituteType',
    'company_id': 'companyId',
    'emp_healthcoverage': 'empHealthcoverage',
    'paas_employee_id': 'paasEmployeeId',
    'ot_rule_code': 'otRuleCode',
    'os_emp_workweek_start_day': 'osEmpWorkweekStartDay',
    'os_employee_group_codes': 'osEmployeeGroupCodes',
    'os_emp_att_rule_codes_json': 'osEmpAttRuleCodesJson',
    'approver_level': 'approverLevel',
    'emp_config_json': 'empConfigJson',
    'emp_pay_codes_json': 'empPayCodesJson',
    'emergency_contact_info': 'emergencyContactInfo',
    'default_pay_code': 'defaultPayCode',
    'is_super_user': 'isSuperUser',
    'emp_payroll_settings_json': 'empPayrollSettingsJson',
    'default_shifts_json': 'defaultShiftsJson',
    'approval_workflow': 'approvalWorkflow',
    'calendar_code': 'calendarCode',
    'signature': 'signature',
    'notification_rule_code': 'notificationRuleCode'
};

/**
 * Attempts to parse a JSON string field if it matches certain criteria
 * Used for handling JSON fields that come as strings from the PHP API
 * 
 * @param {any} value - The value to potentially parse as JSON
 * @param {string} key - The field key/name
 * @returns {any} - Parsed JSON object if successful, original value otherwise
 * 
 * @example
 * parseJsonField('{"foo": "bar"}', 'config_json') // returns { foo: 'bar' }
 * parseJsonField('not json', 'name') // returns 'not json'
 */
const parseJsonField = (value: any, key: string): any => {
    if (typeof value === 'string' && (
        key.toLowerCase().includes('json') ||
        value.startsWith('{') ||
        value.startsWith('[')
    )) {
        try {
            return JSON.parse(value);
        } catch {
            return value;
        }
    }
    return value;
};

/**
 * Transforms PHP API user profile data into the frontend ActorProfileMaster format
 * Handles type conversions, JSON parsing, and field name mapping between PHP and Node.js
 * 
 * Key features:
 * - Maps snake_case PHP fields to camelCase Node.js fields
 * - Converts string numbers to actual numbers
 * - Parses JSON strings into objects
 * - Handles user settings and configuration
 * - Preserves existing user data when merging
 * 
 * @param {any} payload - The raw payload from PHP API containing user data
 * @param {Partial<ActorProfileMaster>} [existingUser] - Optional existing user data to merge with
 * @returns {ActorProfileMaster} The processed and typed user profile data
 * 
 * @throws Will not throw, but will log warning if JSON parsing fails
 * 
 * @example
 * const profile = maskActorProfile({
 *   data: {
 *     os_emp_first_name: "John",
 *     os_emp_last_name: "Doe",
 *     user_account_id: "123",
 *     emp_config_json: '{"allowmobileclocking":true}'
 *   }
 * });
 * 
 * @see {@link ActorProfileMaster} for the complete type definition
 * @see {@link ActorProfile2} for the Node.js field definitions
 */
export const maskActorProfile = (payload: any, existingUser?: Partial<ActorProfileMaster>): ActorProfileMaster => {
    if (!payload?.data) return existingUser as ActorProfileMaster;
    
    const data = payload.data;

    // Start with existing user data if any
    const profile: ActorProfileMaster & Record<string, any> = {
        ...(existingUser || {}),
    };

    // Copy ALL raw data fields first to preserve everything from the API
    Object.keys(data).forEach(key => {
        // Parse JSON fields if needed
        profile[key] = parseJsonField(data[key], key);
    });

    // Then map specific fields to their camelCase versions
    Object.entries(actorProfile2FieldMap).forEach(([phpKey, nodeKey]) => {
        if (data[phpKey] !== undefined) {
            if (numericFields.includes(phpKey as typeof numericFields[number])) {
                // Convert numeric fields to numbers if possible
                if (data[phpKey] !== null && data[phpKey] !== '') {
                    const num = Number(data[phpKey]);
                    if (!isNaN(num)) {
                        (profile as any)[nodeKey] = num;
                    }
                }
            } else {
                // Keep both versions - snake_case and camelCase
                (profile as any)[nodeKey] = parseJsonField(data[phpKey], phpKey);
            }
        }
    });

    // Handle user settings
    let parsedConfigJson: any = {};
    if (data.emp_config_json) {
        try {
            parsedConfigJson = typeof data.emp_config_json === 'string' 
                ? JSON.parse(data.emp_config_json)
                : data.emp_config_json;
        } catch {
            console.warn('Could not parse emp_config_json');
        }
    }

    // Preserve the raw emp_config_json and set userSettings
    profile.userSettings = {
        ...(parsedConfigJson?.userSettings || {}),
        DATETIME_FORMAT: data.DATETIME_FORMAT || "US",
        show_timeclock_in_webview: Boolean(data.show_timeclock_in_webview),
        enable_geo_in_webview: Boolean(data.enable_geo_in_webview),
        EMP_ADD_TIMEOFF: Boolean(data.CAN_ADD_TIMEOFF),
        CAN_ADD_TIMEOFF: Boolean(data.CAN_ADD_TIMEOFF),
        is_otp_enabled: Boolean(data.is_otp_enabled),
        geotrack_threshold: data.geotrack_threshold || 10,
        ngw_employee_mobile_version: data.ngw_employee_mobile_version || 1,
        allowmobileclocking: Boolean(data.allowmobileclocking),
        enforcegeofence: Boolean(data.enforcegeofence),
        is_capturebreak: Boolean(data.capturebreak),
        default_jobCode: data.default_jobCode,
        allowofflineclocking: Boolean(data.allowofflineclocking),
        has_shift_tag: Boolean(data.has_shift_tag),
        geo_policy: data.geo_policy,
        DASHBOARD_WIDGET: data.DASHBOARD_WIDGET,
        app_config: data.app_config
    };

    // Set empConfigJson while preserving raw version
    profile.empConfigJson = {
        ...parsedConfigJson,
        allowmobileclocking: Boolean(data.allowmobileclocking),
        enforcegeofence: Boolean(data.enforcegeofence),
        allowofflineclocking: Boolean(data.allowofflineclocking),
        is_capturebreak: Boolean(data.capturebreak)
    };

    // Preserve all original JSON fields in both formats
    ['default_shifts_json', 'emp_pay_codes_json', 'emp_payroll_settings_json', 'os_emp_att_rule_codes_json'].forEach(jsonField => {
        if (data[jsonField]) {
            const parsed = parseJsonField(data[jsonField], jsonField);
            profile[jsonField] = parsed; // Keep snake_case version
            profile[actorProfile2FieldMap[jsonField]] = parsed; // Keep camelCase version
        }
    });

    // Set remaining fields while preserving originals
    profile.isManager = data.is_manager === true || data.os_employee_role_code === 'MANAGER';
    profile.geo_policy = data.geo_policy;
    profile.app_config = data.app_config;
    profile.display_name = data.display_name || `${data.os_emp_first_name || ''} ${data.os_emp_last_name || ''}`.trim();

    // Ensure important API fields are preserved
    if (data.sites) profile.sites = data.sites;
    if (data.department) profile.department = data.department;
    if (data.jobcode) profile.jobcode = data.jobcode;
    if (data.mobile_timeclock_option_config) profile.mobile_timeclock_option_config = data.mobile_timeclock_option_config;
    if (data.MOBILE_TIMECARD_HOUR_ENTRY) profile.MOBILE_TIMECARD_HOUR_ENTRY = data.MOBILE_TIMECARD_HOUR_ENTRY;
    if (data.country_code) profile.country_code = data.country_code;
    if (data.phone_prefix) profile.phone_prefix = data.phone_prefix;
    if (data.legal_texts_json) profile.legal_texts_json = data.legal_texts_json;
    if (data.profilepicture) profile.profilepicture = data.profilepicture;
    if (data.ics_file) profile.ics_file = data.ics_file;
    if (data.kiosk_clock_code) profile.kiosk_clock_code = data.kiosk_clock_code;

    return profile;
};
